import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCoin } from '../CoinContext';
import CampChatModal from '../components/CampChatModal';
import CampGroupChatModal from '../components/CampGroupChatModal';

// Theme colors
const THEMES = {
  dark: {
    bg: '#121212',
    contentBg: 'rgba(18, 18, 18, 0.98)',
    cardBg: 'rgba(255,255,255,0.05)',
    inputBg: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.12)',
    text: '#e0e0e0',
    textSecondary: '#a0a0a0',
    textMuted: '#707070',
  },
  light: {
    bg: '#f5f5f5',
    contentBg: 'rgba(255, 255, 255, 0.98)',
    cardBg: 'rgba(0,0,0,0.03)',
    inputBg: 'rgba(0,0,0,0.05)',
    border: 'rgba(0,0,0,0.1)',
    text: '#1a1a1a',
    textSecondary: '#555',
    textMuted: '#888',
  },
};

const CONTACT_LABELS = {
  contact_twitter: 'Twitter',
  contact_discord: 'Discord',
  contact_telegram: 'Telegram',
  contact_kakaotalk: 'KakaoTalk',
  contact_line: 'LINE',
  contact_wechat: 'WeChat',
};

const TRANSLATIONS = {
  en: {
    back: 'Back',
    loading: 'Loading...',
    postNotFound: 'Post not found',
    backToCamp: 'Back to Camp',
    anonymous: 'Anonymous',
    languages: 'Languages',
    contact: 'Contact',
    edit: 'Edit',
    delete: 'Delete',
    confirmDelete: 'Are you sure you want to delete this post?',
    deleting: 'Deleting...',
  },
  ko: {
    back: '뒤로',
    loading: '로딩 중...',
    postNotFound: '게시물을 찾을 수 없습니다',
    backToCamp: '캠프로 돌아가기',
    anonymous: '익명',
    languages: '언어',
    contact: '연락처',
    edit: '수정',
    delete: '삭제',
    confirmDelete: '이 글을 삭제하시겠습니까?',
    deleting: '삭제 중...',
  },
  ja: {
    back: '戻る',
    loading: '読み込み中...',
    postNotFound: '投稿が見つかりません',
    backToCamp: 'キャンプに戻る',
    anonymous: '匿名',
    languages: '言語',
    contact: '連絡先',
    edit: '編集',
    delete: '削除',
    confirmDelete: 'この投稿を削除しますか？',
    deleting: '削除中...',
  },
  'zh-TW': {
    back: '返回',
    loading: '載入中...',
    postNotFound: '找不到文章',
    backToCamp: '返回營地',
    anonymous: '匿名',
    languages: '語言',
    contact: '聯絡方式',
    edit: '編輯',
    delete: '刪除',
    confirmDelete: '確定要刪除這篇文章嗎？',
    deleting: '刪除中...',
  },
  'zh-CN': {
    back: '返回',
    loading: '加载中...',
    postNotFound: '找不到帖子',
    backToCamp: '返回营地',
    anonymous: '匿名',
    languages: '语言',
    contact: '联系方式',
    edit: '编辑',
    delete: '删除',
    confirmDelete: '确定要删除这篇帖子吗？',
    deleting: '删除中...',
  },
  th: {
    back: 'กลับ',
    loading: 'กำลังโหลด...',
    postNotFound: 'ไม่พบโพสต์',
    backToCamp: 'กลับไปแคมป์',
    anonymous: 'ไม่ระบุชื่อ',
    languages: 'ภาษา',
    contact: 'ติดต่อ',
    edit: 'แก้ไข',
    delete: 'ลบ',
    confirmDelete: 'คุณแน่ใจหรือไม่ว่าต้องการลบโพสต์นี้?',
    deleting: 'กำลังลบ...',
  },
  vi: {
    back: 'Quay lại',
    loading: 'Đang tải...',
    postNotFound: 'Không tìm thấy bài đăng',
    backToCamp: 'Quay lại Trại',
    anonymous: 'Ẩn danh',
    languages: 'Ngôn ngữ',
    contact: 'Liên hệ',
    edit: 'Chỉnh sửa',
    delete: 'Xóa',
    confirmDelete: 'Bạn có chắc muốn xóa bài đăng này?',
    deleting: 'Đang xóa...',
  },
  id: {
    back: 'Kembali',
    loading: 'Memuat...',
    postNotFound: 'Postingan tidak ditemukan',
    backToCamp: 'Kembali ke Kamp',
    anonymous: 'Anonim',
    languages: 'Bahasa',
    contact: 'Kontak',
    edit: 'Edit',
    delete: 'Hapus',
    confirmDelete: 'Apakah Anda yakin ingin menghapus postingan ini?',
    deleting: 'Menghapus...',
  },
  ms: {
    back: 'Kembali',
    loading: 'Memuatkan...',
    postNotFound: 'Siaran tidak dijumpai',
    backToCamp: 'Kembali ke Kem',
    anonymous: 'Tanpa Nama',
    languages: 'Bahasa',
    contact: 'Hubungi',
    edit: 'Edit',
    delete: 'Padam',
    confirmDelete: 'Adakah anda pasti mahu memadam siaran ini?',
    deleting: 'Memadam...',
  },
  fil: {
    back: 'Bumalik',
    loading: 'Naglo-load...',
    postNotFound: 'Hindi natagpuan ang post',
    backToCamp: 'Bumalik sa Camp',
    anonymous: 'Anonymous',
    languages: 'Mga Wika',
    contact: 'Kontak',
    edit: 'I-edit',
    delete: 'Tanggalin',
    confirmDelete: 'Sigurado ka bang gusto mong tanggalin ang post na ito?',
    deleting: 'Tinatanggal...',
  },
  es: {
    back: 'Volver',
    loading: 'Cargando...',
    postNotFound: 'Publicación no encontrada',
    backToCamp: 'Volver al Campamento',
    anonymous: 'Anónimo',
    languages: 'Idiomas',
    contact: 'Contacto',
    edit: 'Editar',
    delete: 'Eliminar',
    confirmDelete: '¿Estás seguro de que quieres eliminar esta publicación?',
    deleting: 'Eliminando...',
  },
  pt: {
    back: 'Voltar',
    loading: 'Carregando...',
    postNotFound: 'Postagem não encontrada',
    backToCamp: 'Voltar ao Acampamento',
    anonymous: 'Anônimo',
    languages: 'Idiomas',
    contact: 'Contato',
    edit: 'Editar',
    delete: 'Excluir',
    confirmDelete: 'Tem certeza de que deseja excluir esta postagem?',
    deleting: 'Excluindo...',
  },
};

// Helper to get correct path based on subdomain
const getPath = (path) => {
  const isCampSubdomain = window.location.hostname === 'camp.ssalsoongi.com';
  if (isCampSubdomain) {
    return path.replace(/^\/camp/, '') || '/';
  }
  return path;
};

export default function CampPostPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { walletAddress, nickname } = useCoin();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [appliedClass, setAppliedClass] = useState(null);
  const [existingAppliedClass, setExistingAppliedClass] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [isGroupChatOpen, setIsGroupChatOpen] = useState(false);
  const [groupChatId, setGroupChatId] = useState(null);
  const [creatingGroupChat, setCreatingGroupChat] = useState(false);
  const [ownerCP, setOwnerCP] = useState(null);
  const [ownerJobName, setOwnerJobName] = useState(null);
  const [ownerLevel, setOwnerLevel] = useState(null);
  const [uiLanguage] = useState(() => localStorage.getItem('campUiLanguage') || 'en');
  const [theme] = useState(() => {
    const savedMode = localStorage.getItem('campThemeMode');
    const savedTheme = localStorage.getItem('campTheme');

    if (savedMode === 'system' || !savedMode) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return savedTheme || 'dark';
  });

  const colors = THEMES[theme];
  const t = (key) => TRANSLATIONS[uiLanguage]?.[key] || TRANSLATIONS.en[key];

  useEffect(() => {
    fetchPost();
  }, [id]);

  // Fetch owner character stat (CP, jobName, level) with caching
  useEffect(() => {
    if (!post) return;
    const owner = (post.members || []).find(m => m.type === 'owner');
    if (!owner) return;

    // DB에 이미 jobName, level, combatPower가 있으면 API 호출 스킵
    if (owner.jobName && owner.level && owner.combatPower) {
      setOwnerJobName(owner.jobName);
      setOwnerLevel(owner.level);
      setOwnerCP(owner.combatPower);
      return;
    }

    const CACHE_TTL = 5 * 60 * 1000; // 5분 캐시

    const fetchOwnerStat = async () => {
      try {
        let assetKey = owner.assetKey;

        // 1) assetKey가 없으면 getUserCharacters fallback (캐시 적용)
        if (!assetKey && post.author_wallet && owner.name) {
          const charCacheKey = `camp_chars_${post.author_wallet}`;
          const charCached = sessionStorage.getItem(charCacheKey);
          let characters = [];

          if (charCached) {
            const parsed = JSON.parse(charCached);
            if (Date.now() - parsed.ts < CACHE_TTL) {
              characters = parsed.data;
            }
          }

          if (characters.length === 0) {
            const { data: { session } } = await supabase.auth.getSession();
            const headers = session?.access_token
              ? { Authorization: `Bearer ${session.access_token}` }
              : undefined;
            const { data: charData } = await supabase.functions.invoke('getUserCharacters', {
              body: { userId: post.author_wallet },
              ...(headers && { headers }),
            });
            characters = charData?.data?.characters || charData?.data?.data?.characters || [];
            sessionStorage.setItem(charCacheKey, JSON.stringify({ ts: Date.now(), data: characters }));
          }

          const matched = characters.find(c => c.name === owner.name);
          if (matched?.assetKey) {
            assetKey = matched.assetKey;
            // DB에 assetKey 저장 (다음부터 fallback 불필요)
            const updatedMembers = (post.members || []).map(m =>
              m.type === 'owner' ? { ...m, assetKey } : m
            );
            supabase
              .from('camp_posts')
              .update({ members: updatedMembers })
              .eq('id', post.id)
              .then(() => {});
          }
        }

        if (!assetKey) return;

        // 2) getCharacterStat 호출 (캐시 적용)
        const statCacheKey = `camp_stat_${assetKey}`;
        const statCached = sessionStorage.getItem(statCacheKey);
        if (statCached) {
          const parsed = JSON.parse(statCached);
          if (Date.now() - parsed.ts < CACHE_TTL) {
            if (parsed.data.combatPower) setOwnerCP(parsed.data.combatPower);
            if (parsed.data.jobName) setOwnerJobName(parsed.data.jobName);
            if (parsed.data.level) setOwnerLevel(parsed.data.level);
            return;
          }
        }

        const { data: { session } } = await supabase.auth.getSession();
        const headers = session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined;
        const { data, error } = await supabase.functions.invoke('getCharacterStat', {
          body: { assetKey },
          ...(headers && { headers }),
        });
        if (!error && data?.success && data?.data) {
          sessionStorage.setItem(statCacheKey, JSON.stringify({ ts: Date.now(), data: data.data }));
          if (data.data.combatPower) setOwnerCP(data.data.combatPower);
          if (data.data.jobName) setOwnerJobName(data.data.jobName);
          if (data.data.level) setOwnerLevel(data.data.level);
        }
      } catch (err) {
        console.error('Failed to fetch owner stat:', err);
      }
    };
    fetchOwnerStat();
  }, [post]);

  // Check if user already applied to this post
  useEffect(() => {
    if (!walletAddress || !id) return;
    const checkExistingApplication = async () => {
      try {
        const { data } = await supabase
          .from('camp_chat_rooms')
          .select('applied_class')
          .eq('post_id', id)
          .or(`user1_wallet.eq.${walletAddress},user2_wallet.eq.${walletAddress}`)
          .limit(1);
        if (data && data.length > 0 && data[0].applied_class) {
          setExistingAppliedClass(data[0].applied_class);
        }
      } catch (err) {
        console.error('Failed to check application:', err);
      }
    };
    checkExistingApplication();
  }, [walletAddress, id]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('camp_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setPost(data);
    } catch (err) {
      console.error('Failed to fetch post:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecruitStatusChange = async (slotClass, newStatus) => {
    if (!post) return;
    try {
      let current = [...(post.recruited_classes || [])];
      if (newStatus === 'recruited') {
        if (!current.includes(slotClass)) {
          current.push(slotClass);
        }
      } else {
        current = current.filter(c => c !== slotClass);
      }

      const { data, error } = await supabase
        .from('camp_posts')
        .update({ recruited_classes: current })
        .eq('id', post.id)
        .eq('author_wallet', walletAddress)
        .select();
      if (error) throw error;
      if (!data || data.length === 0) {
        alert('업데이트 실패. RLS 정책을 확인해주세요.');
        return;
      }
      setPost({ ...post, recruited_classes: current });
    } catch (err) {
      console.error('Failed to update recruit status:', err);
      alert('모집 상태 변경에 실패했습니다.');
    }
  };

  const handleCreateGroupChat = async () => {
    if (!post || !walletAddress || creatingGroupChat) return;
    setCreatingGroupChat(true);
    try {
      // Check if group chat already exists for this post
      const { data: existing } = await supabase
        .from('camp_group_chats')
        .select('id')
        .eq('post_id', post.id)
        .limit(1);

      if (existing && existing.length > 0) {
        setGroupChatId(existing[0].id);
        setIsGroupChatOpen(true);
        setCreatingGroupChat(false);
        return;
      }

      // Get recruited users from chat rooms
      const recruitedClasses = post.recruited_classes || [];
      const { data: chatRooms, error: roomErr } = await supabase
        .from('camp_chat_rooms')
        .select('*')
        .eq('post_id', post.id);
      if (roomErr) throw roomErr;

      // Filter chat rooms where applied_class is recruited
      const recruitedRooms = (chatRooms || []).filter(
        (r) => r.applied_class && recruitedClasses.includes(r.applied_class)
      );

      if (recruitedRooms.length === 0) {
        alert('모집 완료된 유저가 없습니다.');
        setCreatingGroupChat(false);
        return;
      }

      // Create group chat
      const { data: groupChat, error: createErr } = await supabase
        .from('camp_group_chats')
        .insert({
          post_id: post.id,
          name: post.title,
          created_by: walletAddress,
        })
        .select()
        .single();
      if (createErr) throw createErr;

      // Gather unique members (author + recruited users)
      const memberEntries = [];
      const addedWallets = new Set();

      // Add author
      memberEntries.push({
        group_chat_id: groupChat.id,
        wallet_address: walletAddress,
        nickname: post.author_nickname || nickname || 'Anonymous',
        character_class: 'Owner',
      });
      addedWallets.add(walletAddress);

      // Add recruited users
      for (const room of recruitedRooms) {
        const otherWallet = room.user1_wallet === walletAddress
          ? room.user2_wallet : room.user1_wallet;
        const otherNickname = room.user1_wallet === walletAddress
          ? room.user2_nickname : room.user1_nickname;

        if (!addedWallets.has(otherWallet)) {
          memberEntries.push({
            group_chat_id: groupChat.id,
            wallet_address: otherWallet,
            nickname: otherNickname || 'Anonymous',
            character_class: room.applied_class || null,
          });
          addedWallets.add(otherWallet);
        }
      }

      const { error: memErr } = await supabase
        .from('camp_group_chat_members')
        .insert(memberEntries);
      if (memErr) throw memErr;

      setGroupChatId(groupChat.id);
      setIsGroupChatOpen(true);
    } catch (err) {
      console.error('Failed to create group chat:', err);
      alert('파티 채팅 생성에 실패했습니다.');
    } finally {
      setCreatingGroupChat(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: colors.bg,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 430,
            minHeight: '100vh',
            background: colors.contentBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.textSecondary,
            boxShadow: '0 0 40px rgba(0,0,0,0.3)',
          }}
        >
          {t('loading')}
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div
        style={{
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: colors.bg,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 430,
            minHeight: '100vh',
            background: colors.contentBg,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.textSecondary,
            gap: 20,
            boxShadow: '0 0 40px rgba(0,0,0,0.3)',
          }}
        >
          <p>{t('postNotFound')}</p>
          <button
            onClick={() => navigate(getPath('/camp'))}
            style={{
              background: colors.inputBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: '10px 20px',
              cursor: 'pointer',
              color: colors.text,
              fontSize: 14,
            }}
          >
            {t('backToCamp')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'hidden',
        background: colors.bg,
      }}
    >
      {/* Main Content Area */}
      <div
        style={{
          width: '100%',
          maxWidth: 430,
          minHeight: '100vh',
          background: colors.contentBg,
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          zIndex: 2,
          boxShadow: '0 0 40px rgba(0,0,0,0.3)',
        }}
      >
      {/* Header */}
      <div
        style={{
          width: '100%',
          maxWidth: 430,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <button
          onClick={() => navigate(getPath('/camp'))}
          style={{
            background: colors.inputBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            padding: '10px 16px',
            cursor: 'pointer',
            color: colors.text,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          ← {t('back')}
        </button>
      </div>

      {/* Post Content */}
      <div
        style={{
          width: '100%',
          maxWidth: 430,
          background: colors.cardBg,
          borderRadius: 16,
          padding: 20,
        }}
      >
        <h1
          style={{
            margin: 0,
            marginBottom: 12,
            fontSize: 20,
            fontWeight: 700,
            color: colors.text,
            lineHeight: 1.4,
          }}
        >
          {post.title}
        </h1>

        {(post.server || post.boss) && (
          <div
            style={{
              fontSize: 14,
              color: '#667eea',
              marginBottom: 12,
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            {post.server && (
              <span
                style={{
                  background: 'rgba(102, 126, 234, 0.2)',
                  padding: '4px 10px',
                  borderRadius: 8,
                }}
              >
                {post.server}
              </span>
            )}
            {post.boss && (
              <span
                style={{
                  background: 'rgba(118, 75, 162, 0.2)',
                  padding: '4px 10px',
                  borderRadius: 8,
                }}
              >
                {post.boss}
              </span>
            )}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
            paddingBottom: 16,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <span style={{ color: colors.textMuted, fontSize: 13 }}>
            {post.author_nickname || t('anonymous')}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: colors.textMuted, fontSize: 12 }}>
              {formatDate(post.created_at)}
            </span>
            {walletAddress && post.author_wallet === walletAddress && (
              <>
                <button
                  onClick={() => navigate(getPath(`/camp/edit/${post.id}`))}
                  style={{
                    background: colors.inputBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 8,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    color: colors.textSecondary,
                    fontSize: 12,
                  }}
                >
                  {t('edit')}
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm(t('confirmDelete'))) return;
                    setDeleting(true);
                    try {
                      const { error } = await supabase
                        .from('camp_posts')
                        .delete()
                        .eq('id', post.id)
                        .eq('author_wallet', walletAddress);
                      if (error) throw error;
                      navigate(getPath('/camp'), { replace: true });
                    } catch (err) {
                      console.error('Failed to delete:', err);
                      alert('Failed to delete post.');
                      setDeleting(false);
                    }
                  }}
                  disabled={deleting}
                  style={{
                    background: 'rgba(244, 67, 54, 0.15)',
                    border: '1px solid rgba(244, 67, 54, 0.4)',
                    borderRadius: 8,
                    padding: '4px 10px',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    color: '#f44336',
                    fontSize: 12,
                    opacity: deleting ? 0.6 : 1,
                  }}
                >
                  {deleting ? t('deleting') : t('delete')}
                </button>
              </>
            )}
          </div>
        </div>

        <div
          style={{
            color: colors.textSecondary,
            fontSize: 14,
            lineHeight: 1.8,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {post.content}
        </div>

        {/* Languages */}
        {post.languages && post.languages.length > 0 && (
          <div
            style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: `1px solid ${colors.border}`,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.textMuted,
                marginBottom: 10,
              }}
            >
              {t('languages')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {post.languages.map((lang) => (
                <span
                  key={lang}
                  style={{
                    fontSize: 11,
                    color: colors.textSecondary,
                    background: colors.inputBg,
                    padding: '4px 10px',
                    borderRadius: 12,
                  }}
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Members */}
        {post.members && post.members.length > 0 && (() => {
          const recruitSlots = post.members.filter(m => m.type === 'recruit');
          const recruitedClasses = post.recruited_classes || [];
          const allRecruited = recruitSlots.length > 0 && recruitSlots.every(m => recruitedClasses.includes(m.character_class || 'Any Class'));
          return (
          <div
            style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: `1px solid ${colors.border}`,
            }}
          >
            <div style={{
              fontSize: 13, fontWeight: 600, color: colors.textMuted, marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              Members
              {post.member_count && (
                <span style={{
                  background: 'rgba(102, 126, 234, 0.2)', color: '#667eea',
                  fontSize: 11, padding: '2px 8px', borderRadius: 10,
                }}>
                  {post.member_count}
                </span>
              )}
              {allRecruited && (
                <span style={{
                  background: 'rgba(34,197,94,0.15)', color: '#22c55e',
                  fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                }}>
                  모집 완료
                </span>
              )}
            </div>
            {allRecruited && walletAddress && post.author_wallet === walletAddress && (
              <button
                onClick={handleCreateGroupChat}
                disabled={creatingGroupChat}
                style={{
                  width: '100%',
                  marginBottom: 10,
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: creatingGroupChat ? 'not-allowed' : 'pointer',
                  opacity: creatingGroupChat ? 0.6 : 1,
                }}
              >
                {creatingGroupChat ? '생성 중...' : 'Party Chat'}
              </button>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {post.members.map((member, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: colors.inputBg,
                    borderRadius: 10, padding: '8px 12px',
                  }}
                >
                  <span style={{ fontSize: 12, color: colors.textMuted, minWidth: 18, textAlign: 'center', fontWeight: 600 }}>
                    {i + 1}
                  </span>
                  {member.type === 'owner' ? (
                    <>
                      {member.imageUrl && (
                        <div style={{
                          width: 32, height: 32, borderRadius: 8, overflow: 'hidden',
                          backgroundColor: colors.cardBg, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <img src={member.imageUrl} alt="" style={{
                            width: '140%', height: '140%', objectFit: 'contain', transform: 'translateY(-4px)',
                          }} />
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 13, color: colors.text, fontWeight: 600 }}>
                            {member.name || post.author_nickname || t('anonymous')}
                          </span>
                          <span style={{
                            fontSize: 10, color: '#667eea', background: 'rgba(102,126,234,0.15)',
                            padding: '2px 6px', borderRadius: 6,
                          }}>Owner</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                          {(ownerJobName || member.jobName || member.jobCode) && (
                            <span style={{ fontSize: 11, color: '#a78bfa', fontWeight: 500 }}>
                              {ownerJobName || member.jobName || `Job ${member.jobCode}`}
                            </span>
                          )}
                          {(ownerLevel || member.level) && (
                            <span style={{ fontSize: 11, color: colors.textMuted }}>Lv.{ownerLevel || member.level}</span>
                          )}
                          {(ownerCP || member.combatPower) && (
                            <span style={{
                              fontSize: 11, color: '#f59e0b', fontWeight: 600,
                            }}>
                              CP {Number(ownerCP || member.combatPower).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 13, color: colors.text, fontWeight: 500, flex: 1 }}>
                        {member.character_class || 'Any Class'}
                      </span>
                      {member.cp && (
                        <span style={{ fontSize: 12, color: colors.textMuted }}>CP {member.cp.toLocaleString()}</span>
                      )}
                      {member.level && (
                        <span style={{ fontSize: 12, color: colors.textMuted }}>Lv.{member.level}</span>
                      )}
                      {(() => {
                        const slotClass = member.character_class || 'Any Class';
                        const recruitedClasses = post.recruited_classes || [];
                        const isSlotRecruited = recruitedClasses.includes(slotClass);
                        const isAuthor = walletAddress && post.author_wallet === walletAddress;

                        // 글 작성자: 드롭다운으로 모집 상태 변경
                        if (isAuthor) {
                          return (
                            <select
                              value={isSlotRecruited ? 'recruited' : 'recruiting'}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleRecruitStatusChange(slotClass, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: '4px 6px',
                                borderRadius: 8,
                                cursor: 'pointer',
                                outline: 'none',
                                flexShrink: 0,
                                border: isSlotRecruited
                                  ? '1px solid rgba(34,197,94,0.4)'
                                  : `1px solid ${colors.border}`,
                                background: isSlotRecruited
                                  ? 'rgba(34,197,94,0.15)'
                                  : colors.inputBg,
                                color: isSlotRecruited ? '#22c55e' : colors.text,
                              }}
                            >
                              <option value="recruiting">모집중</option>
                              <option value="recruited">모집 완료</option>
                            </select>
                          );
                        }

                        // 모집 완료된 슬롯 (비작성자)
                        if (isSlotRecruited) {
                          return (
                            <span
                              style={{
                                background: 'rgba(34,197,94,0.15)',
                                border: '1px solid rgba(34,197,94,0.4)',
                                borderRadius: 8,
                                padding: '4px 10px',
                                color: '#22c55e',
                                fontSize: 11,
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              모집 완료
                            </span>
                          );
                        }

                        // 비로그인 유저
                        if (!walletAddress || !post.author_wallet) return null;

                        const isThisSlotApplied = existingAppliedClass === slotClass;
                        const hasAppliedOther = existingAppliedClass && !isThisSlotApplied;
                        if (isThisSlotApplied) {
                          return (
                            <span
                              style={{
                                background: 'rgba(102, 126, 234, 0.15)',
                                border: '1px solid rgba(102, 126, 234, 0.4)',
                                borderRadius: 8,
                                padding: '4px 10px',
                                color: '#667eea',
                                fontSize: 11,
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              Applied
                            </span>
                          );
                        }
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setAppliedClass(slotClass);
                              setIsChatOpen(true);
                            }}
                            disabled={!!hasAppliedOther}
                            style={{
                              background: hasAppliedOther
                                ? colors.cardBg
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              border: 'none',
                              borderRadius: 8,
                              padding: '4px 10px',
                              cursor: hasAppliedOther ? 'not-allowed' : 'pointer',
                              color: hasAppliedOther ? colors.textMuted : '#fff',
                              fontSize: 11,
                              fontWeight: 600,
                              flexShrink: 0,
                              opacity: hasAppliedOther ? 0.5 : 1,
                            }}
                          >
                            Apply
                          </button>
                        );
                      })()}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
          );
        })()}

        {/* Contact Links */}
        {Object.keys(CONTACT_LABELS).some(key => post[key]) && (
          <div
            style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: `1px solid ${colors.border}`,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.textMuted,
                marginBottom: 12,
              }}
            >
              {t('contact')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Object.entries(CONTACT_LABELS).map(([key, label]) => {
                const value = post[key];
                if (!value) return null;
                const isLink = value.startsWith('http');
                return (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        width: 80,
                        fontSize: 12,
                        color: colors.textMuted,
                        flexShrink: 0,
                      }}
                    >
                      {label}
                    </span>
                    {isLink ? (
                      <a
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#667eea',
                          fontSize: 13,
                          textDecoration: 'none',
                          wordBreak: 'break-all',
                        }}
                      >
                        {value}
                      </a>
                    ) : (
                      <span
                        style={{
                          color: colors.textSecondary,
                          fontSize: 13,
                          wordBreak: 'break-all',
                        }}
                      >
                        {value}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Group Chat Modal */}
      <CampGroupChatModal
        isOpen={isGroupChatOpen}
        onClose={() => setIsGroupChatOpen(false)}
        groupChatId={groupChatId}
        postId={post?.id}
        postTitle={post?.title}
      />

      {/* Chat Modal */}
      <CampChatModal
        isOpen={isChatOpen}
        onClose={(result) => {
          setIsChatOpen(false);
          if (result?.permanentlyDeleted) {
            setExistingAppliedClass(null);
          } else if (appliedClass) {
            setExistingAppliedClass(appliedClass);
          }
          setAppliedClass(null);
          // 모집 완료 상태 반영을 위해 글 다시 fetch
          fetchPost();
        }}
        targetWallet={post?.author_wallet}
        targetNickname={post?.author_nickname}
        postId={post?.id}
        postTitle={post?.title}
        appliedClass={appliedClass}
      />
    </div>
  );
}
